/**
 * Страница "Семьи пациентов": список семей, поиск, создание из дубликатов
 * Дата: 09.01.2026 - Создан для TODO-029 (Patient Families)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/families.ts - API для работы с семьями
 * - frontend/src/hooks/useFamilies.ts - хук для управления состоянием семей
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * 
 * КАК РАБОТАЕТ:
 * - Отображает список семей с поиском
 * - Создание/редактирование/удаление семей
 * - Вкладка "Потенциальные семьи" для работы с дубликатами
 * 
 * СХЕМА:
 * PatientFamiliesPage → useFamilies → familiesApi → backend API
 * PatientFamiliesPage → SearchInput → фильтрация filteredFamilies
 */
import { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardHeader, CardTitle, CardContent, Button, TenantSelector, SearchInput, EmptyState, LoadingState } from '@/components/ui';
import { FamilyCard } from '@/components/features/families/FamilyCard';
import { FamilyModal } from '@/components/features/families/FamilyModal';
import { DuplicatesTab } from '@/components/features/families/DuplicatesTab';
import { useFamilies, useDeleteFamily, useSetPrimaryContact } from '@/hooks/useFamilies';
import { useTenantScope } from '@/hooks/useTenantScope';
import { PatientFamily } from '@/api/types';
import { Plus, Users } from 'lucide-react';
import { Can } from '@/components/Can';
import { PageGuide, HelpTooltip } from '@/components/ui/help';
import { familiesHelp } from '@/content/help';

export function PatientFamiliesPage() {
  const { tenantId: effectiveTenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const [activeTab, setActiveTab] = useState<'families' | 'duplicates'>('families');
  const [showGuide, setShowGuide] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingFamily, setEditingFamily] = useState<PatientFamily | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading } = useFamilies(effectiveTenantId);
  const deleteFamily = useDeleteFamily();
  const setPrimary = useSetPrimaryContact();

  const families = data?.families || [];
  const total = data?.total || 0;

  // Фильтрация по поисковому запросу
  const filteredFamilies = families.filter((family) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      family.name.toLowerCase().includes(query) ||
      family.phone_normalized.includes(query) ||
      family.members?.some((m) =>
        m.patient?.full_name?.toLowerCase().includes(query)
      )
    );
  });

  const handleDelete = (id: string) => {
    if (confirm('Вы уверены, что хотите удалить эту семью?')) {
      deleteFamily.mutate({ id, tenantId: effectiveTenantId });
    }
  };

  const handleSetPrimary = (familyId: string, patientId: string) => {
    setPrimary.mutate({ familyId, patientId, tenantId: effectiveTenantId });
  };

  return (
    <MainLayout
      headerTitle="Семьи пациентов"
      headerActions={
        <div className="flex items-center gap-4">
          {isSuperAdmin && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-700 whitespace-nowrap">Клиника</span>
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                required={false}
                showLabel={false}
              />
            </div>
          )}
          <Can permission="families:write">
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              testId="btn-create-family"
              disabled={requireTenantSelection}
            >
              <Plus className="w-4 h-4 mr-2" />
              Создать семью
            </Button>
          </Can>
        </div>
      }
    >
      <div className="space-y-4 p-4">
        <button type="button" className="shell-button-secondary h-9" onClick={() => setShowGuide((open) => !open)}>
          Справка
        </button>
        {showGuide && (
        <PageGuide
          id="families-guide"
          title={familiesHelp.pageGuide.title}
          variant="info"
          expandedContent={
            familiesHelp.expandedGuide && (
              <div className="space-y-1.5">
                {familiesHelp.expandedGuide.body.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            )
          }
        >
          {familiesHelp.pageGuide.body.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </PageGuide>
        )}

        {/* Вкладки */}
        <div className="flex gap-2 mb-6 border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('families')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'families'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
            test-id="tab-families"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Семьи ({total})
          </button>
          <button
            onClick={() => setActiveTab('duplicates')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'duplicates'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
            test-id="tab-duplicates"
          >
            Потенциальные семьи
            <HelpTooltip content={familiesHelp.tooltips.potential_families} side="bottom" />
          </button>
        </div>

        {/* Контент вкладок */}
        {activeTab === 'families' ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Список семей</CardTitle>
                <div className="w-64">
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Поиск по имени или телефону..."
                    testId="input-search-families"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {requireTenantSelection ? (
                <EmptyState
                  type="noData"
                  title="Выберите клинику"
                  description="Выберите клинику для просмотра семей пациентов"
                />
              ) : isLoading ? (
                <LoadingState />
              ) : filteredFamilies.length === 0 ? (
                <EmptyState 
                  type="search"
                  title={searchQuery ? 'Ничего не найдено' : 'Нет семей'}
                  description={searchQuery 
                    ? 'Попробуйте изменить параметры поиска' 
                    : 'Создайте первую семью или найдите потенциальные семьи во вкладке "Потенциальные семьи"'}
                  actionText="Создать семью"
                  onAction={() => setIsCreateModalOpen(true)}
                />
              ) : (
                <div>
                  {filteredFamilies.map((family) => (
                    <FamilyCard
                      key={family.id}
                      family={family}
                      onEdit={setEditingFamily}
                      onDelete={handleDelete}
                      onSetPrimary={handleSetPrimary}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          requireTenantSelection ? (
            <Card>
              <CardContent className="pt-6">
                <EmptyState
                  type="noData"
                  title="Выберите клинику"
                  description="Выберите клинику для просмотра потенциальных семей"
                />
              </CardContent>
            </Card>
          ) : (
            <DuplicatesTab tenantId={effectiveTenantId} />
          )
        )}

        {/* Модалки */}
        <FamilyModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          tenantId={effectiveTenantId}
        />
        {editingFamily && (
          <FamilyModal
            isOpen={!!editingFamily}
            onClose={() => setEditingFamily(null)}
            family={editingFamily}
            tenantId={effectiveTenantId}
          />
        )}
      </div>
    </MainLayout>
  );
}

